import type { Row } from "./types";
import { TEHO_CONTRACTS } from "./teho-data";
import { NCLEAN_CONTRACTS } from "./nclean-data";
export type ColumnType =
  "VARCHAR" | "DOUBLE" | "INTEGER" | "BOOLEAN" | "DATE" | "TIMESTAMP";
export interface ContractColumn {
  label?: string;
  type: ColumnType;
  nullable: boolean;
  lineage: string;
  description: string;
}
export interface DataContract {
  source: string;
  label: string;
  grain: string;
  key: string;
  columns: Record<string, ContractColumn>;
}
const col = (
  type: ColumnType,
  lineage: string,
  description: string,
  nullable = false,
): ContractColumn => ({ type, lineage, description, nullable });
const shared = {
  operating_unit_id: col(
    "VARCHAR",
    "Shift.operatingUnitId / Property.operatingUnitId / SupplyOrder.operatingUnitId",
    "Operating Unit identity",
  ),
  property_id: col("VARCHAR", "Property.id", "Property identity"),
  property_name: col(
    "VARCHAR",
    "Property.name",
    "Current property display name",
  ),
  unit_id: col("VARCHAR", "PropertyUnit.id", "Property unit identity", true),
  unit_name: col(
    "VARCHAR",
    "PropertyUnit.name",
    "Property unit display name",
    true,
  ),
  unit_type: col(
    "VARCHAR",
    "PropertyUnit.unitTypeId -> UnitType.name",
    "Current unit classification",
    true,
  ),
  snapshot_at: col(
    "TIMESTAMP",
    "Materialization snapshot",
    "Fixed as-of timestamp in UTC",
  ),
};
const linked = {
  ...shared,
  unit_id: { ...shared.unit_id, nullable: false },
  unit_name: { ...shared.unit_name, nullable: false },
};
const date = col(
  "DATE",
  "CleaningTask.startsAt ?? Shift.startsAt ?? CleaningTask.createdAt; Property.timezone ?? OperatingUnit.timezone",
  "Task-cohort date; unlinked records use their own event date",
);
export const CONTRACTS: Record<string, DataContract> = {
  ...NCLEAN_CONTRACTS,
  ...TEHO_CONTRACTS,
  mv_cleaning_tasks: {
    source: "cleaning_tasks",
    label: "Cleaning tasks",
    grain: "One CleaningTask.id",
    key: "id",
    columns: {
      id: col("VARCHAR", "CleaningTask.id", "Task identity"),
      ...linked,
      work_date: date,
      status: col("VARCHAR", "CleaningTask.status", "Task state at snapshot"),
      started_at: col(
        "TIMESTAMP",
        "CleaningTask.actualStartedAt",
        "Actual task start",
        true,
      ),
      completed_at: col(
        "TIMESTAMP",
        "CleaningTask.completedAt",
        "Recorded server completion",
        true,
      ),
      deadline: col(
        "TIMESTAMP",
        "CleaningTask.deadline",
        "Task deadline",
        true,
      ),
      participant_count: col(
        "INTEGER",
        "CleanerCleaningSession.employeeId",
        "Distinct employees with a session",
      ),
      participants: col(
        "VARCHAR",
        "CleanerCleaningSession.employeeId -> Employee",
        "Session participant names",
      ),
      recorded_minutes: col(
        "DOUBLE",
        "CleanerCleaningSession.startedAt, endedAt",
        "Sum of closed per-person spans, including breaks",
      ),
      break_minutes: col(
        "DOUBLE",
        "CleanerSessionBreak.startedAt, endedAt",
        "Union of observed intervals bounded to sessions and as-of",
      ),
      invalid_session_count: col(
        "INTEGER",
        "CleanerCleaningSession.startedAt, endedAt",
        "Sessions with invalid timestamps",
      ),
      open_session_count: col(
        "INTEGER",
        "CleanerCleaningSession.endedAt",
        "Open sessions excluded from closed effort",
      ),
      invalid_completion: col(
        "BOOLEAN",
        "CleaningTask.completedAt, actualStartedAt, status",
        "Missing/invalid completion evidence",
      ),
      completed: col(
        "BOOLEAN",
        "CleaningTask.status, completedAt",
        "Completed with valid evidence by as-of",
      ),
      due: col(
        "BOOLEAN",
        "CleaningTask.status, deadline",
        "Non-cancelled task due by as-of",
      ),
      on_time: col(
        "BOOLEAN",
        "CleaningTask.completedAt, deadline, status",
        "Due task completed by deadline",
      ),
      has_inspection: col(
        "BOOLEAN",
        "InspectorTask.sourceCleanerTaskId -> InspectionResult.submittedAt",
        "Submitted linked result by as-of",
      ),
      first_outcome: col(
        "VARCHAR",
        "InspectionResult.outcome",
        "Earliest submitted linked result; timestamp then result ID",
        true,
      ),
      first_pass: col(
        "BOOLEAN",
        "InspectionResult.outcome",
        "First outcome is CLEAN",
        true,
      ),
      submitted_orders: col(
        "INTEGER",
        "SupplyOrder.taskId, status",
        "Linked submitted orders",
      ),
      oldest_order_minutes: col(
        "DOUBLE",
        "SupplyOrder.createdAt",
        "Oldest submitted-order age, not cleaner waiting",
        true,
      ),
      supply_state: col(
        "VARCHAR",
        "CleanerSupplyReview.state",
        "Supply review state",
        true,
      ),
    },
  },
  mv_cleaner_sessions: {
    source: "cleaner_sessions",
    label: "Cleaner sessions",
    grain: "One (CleaningTask.id, employeeId)",
    key: "session_key",
    columns: {
      session_key: col(
        "VARCHAR",
        "CleanerCleaningSession.taskId, employeeId",
        "JSON-encoded composite key",
      ),
      ...linked,
      work_date: date,
      task_id: col("VARCHAR", "CleanerCleaningSession.taskId", "Owning task"),
      employee_id: col(
        "VARCHAR",
        "CleanerCleaningSession.employeeId",
        "Employee identity",
      ),
      employee_name: col("VARCHAR", "Employee.name", "Employee display name"),
      assignment_id: col(
        "VARCHAR",
        "CleanerCleaningSession.assignmentId",
        "ShiftAssignment identity",
      ),
      status: col("VARCHAR", "CleanerCleaningSession.status", "Session state"),
      started_at: col(
        "TIMESTAMP",
        "CleanerCleaningSession.startedAt",
        "Session start",
        true,
      ),
      ended_at: col(
        "TIMESTAMP",
        "CleanerCleaningSession.endedAt",
        "Session end by as-of",
        true,
      ),
      elapsed_minutes: col(
        "DOUBLE",
        "CleanerCleaningSession.startedAt, endedAt",
        "Closed-session person-minutes including breaks",
        true,
      ),
      observed_minutes: col(
        "DOUBLE",
        "CleanerCleaningSession.startedAt, endedAt",
        "Observed span through as-of including open sessions",
        true,
      ),
      break_minutes: col(
        "DOUBLE",
        "CleanerSessionBreak.startedAt, endedAt",
        "Union of observed break intervals",
      ),
      coffee_minutes: col(
        "DOUBLE",
        "CleanerSessionBreak.type, startedAt, endedAt",
        "Observed coffee1/coffee2 intervals",
      ),
      meal_minutes: col(
        "DOUBLE",
        "CleanerSessionBreak.type, startedAt, endedAt",
        "Observed meal intervals",
      ),
      invalid_timestamp: col(
        "BOOLEAN",
        "CleanerCleaningSession.startedAt, endedAt",
        "Invalid timestamp evidence",
      ),
      invalid_break_count: col(
        "INTEGER",
        "CleanerSessionBreak.startedAt, endedAt",
        "Invalid break intervals",
      ),
      open_session: col(
        "BOOLEAN",
        "CleanerCleaningSession.endedAt",
        "No end evidence by as-of",
      ),
    },
  },
  mv_inspections: {
    source: "inspections",
    label: "Inspections",
    grain: "One InspectorTask.id with optional result",
    key: "id",
    columns: {
      id: col("VARCHAR", "InspectorTask.id", "Inspection task identity"),
      ...linked,
      work_date: date,
      task_id: col(
        "VARCHAR",
        "InspectorTask.sourceCleanerTaskId",
        "Optional cleaning task",
        true,
      ),
      source_result_id: col(
        "VARCHAR",
        "InspectorTask.sourceResultId",
        "Reinspection lineage",
        true,
      ),
      result_id: col("VARCHAR", "InspectionResult.id", "Result identity", true),
      status: col("VARCHAR", "InspectorTask.status", "Inspection state"),
      outcome: col(
        "VARCHAR",
        "InspectionResult.outcome",
        "Cleaning-state outcome",
        true,
      ),
      rating: col(
        "INTEGER",
        "InspectionResult.rating",
        "Raw rating without assumed scale",
        true,
      ),
      submitted_at: col(
        "TIMESTAMP",
        "InspectionResult.submittedAt",
        "Submission by as-of",
        true,
      ),
      first_inspection: col(
        "BOOLEAN",
        "InspectorTask.sourceCleanerTaskId, InspectionResult.submittedAt, id",
        "Earliest linked result",
      ),
    },
  },
  mv_current_units: {
    source: "current_units",
    label: "Current units",
    grain: "One active PropertyUnit.id at as-of",
    key: "id",
    columns: {
      id: col("VARCHAR", "PropertyUnit.id", "Unit identity"),
      ...linked,
      cleaning_state: col(
        "VARCHAR",
        "PropertyUnit.cleaningState",
        "CLEAN / DIRTY / IN_PROGRESS / INSPECTION",
      ),
      state_changed_at: col(
        "TIMESTAMP",
        "PropertyUnit.changedAt",
        "Latest state change by as-of",
        true,
      ),
    },
  },
  mv_supply_orders: {
    source: "supply_orders",
    label: "Supply orders",
    grain: "One SupplyOrder.id",
    key: "id",
    columns: {
      id: col("VARCHAR", "SupplyOrder.id", "Order identity"),
      ...shared,
      work_date: date,
      task_id: col(
        "VARCHAR",
        "SupplyOrder.taskId",
        "Optional cleaning task",
        true,
      ),
      status: col(
        "VARCHAR",
        "SupplyOrder.status",
        "submitted / fulfilled / cancelled",
      ),
      priority: col("VARCHAR", "SupplyOrder.priority", "Order priority"),
      created_at: col(
        "TIMESTAMP",
        "SupplyOrder.createdAt",
        "Creation timestamp",
      ),
      age_minutes: col(
        "DOUBLE",
        "SupplyOrder.createdAt, materialization as-of",
        "Submitted-order age only",
        true,
      ),
    },
  },
};
export type Snapshots = Record<string, Row[]>;
